import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import assessmentReader from '../../store/readHelpers/AssessmentReader';
import LoadTracker from '../../services/LoadTracker';
import scrollingService from '../../services/ScrollingService';
import settingsReader from '../../store/readHelpers/SettingsReader';
import sessionReader from '../../store/readHelpers/SessionReader';
import questionReader from '../../store/readHelpers/QuestionReader';
import viewReadyTracker from '../../services/ViewReadyTracker';
import QuestionDisplayContainer from '../question/QuestionDisplay';
import { StimulusDisplayContainer } from '../stimulusDisplay/StimulusDisplay';
import TableOfContents from '../tableOfContents/TableOfContents';
import ResultPageContainer from '../result/ResultPage';
import TestCover from '../testCover/TestCover';
import utilService from '../../services/UtilService';

import styles from './allView.scss';
import objectTypes from '../../constants/ObjectTypes';
import AnswerKeys from '../answerKeys/AnswerKeys';
import allViewContainerSelector from '../../selectors/allView/AllViewContainerSelector';
import appMainScreenSelector from '../../selectors/app/AppMainScreenSelector';
import uiService from '../../services/UIService';

export class AllView extends React.Component {
    static propTypes = {
        deliveryDetails: PropTypes.object,
        itemsOrder: PropTypes.array,
        shouldRenderAnswerKeys: PropTypes.bool.isRequired,
        shouldRenderTestItems: PropTypes.bool.isRequired,
        shouldRenderTestCover: PropTypes.bool.isRequired,
        shouldRenderResultPageContainer: PropTypes.bool.isRequired,
        shouldRenderTableOfContents: PropTypes.bool.isRequired,
        extraClasses: PropTypes.string,
    };

    componentDidMount() {
        const { shouldRenderAnswerKeys } = this.props;
        if (shouldRenderAnswerKeys) {
            this.onReady(true);
        }
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        const { shouldRenderAnswerKeys } = this.props;
        if (!shouldRenderAnswerKeys) {
            this.filteredItemsOrder = this.filterUnWantedItems(this.props.itemsOrder);
            this.initLoadTracker(this.filteredItemsOrder);
        }
    }

    onReady(success) {
        scrollingService.scrollContentToTop(this.rootElement);
        viewReadyTracker.getReporter()(success);
    }

    filterUnWantedItems(itemsOrder) {
        const isPrintOrPdf = sessionReader.isViewMediaPrintOrPdf();
        const selectedQuestionId = parseInt(sessionReader.getPrintParam('selectedQuestionId'), 10);
        return itemsOrder.filter((item) => {
            const question = assessmentReader.getQuestion(item.contentRevisionId);

            if (appMainScreenSelector.selectIsMarksOnlyAccess() && item.type !== objectTypes.CATEGORY.STIMULUS) {
                return false;
            }

            if (!settingsReader.hasSettingValue(sessionReader.printParamNames.SHOW_DOCUMENTS, true)
                && questionReader.isDocument(question) && !questionReader.isStimulus(question)) {
                return false;
            }

            if (settingsReader.hasSettingValue(sessionReader.printParamNames.SHOW_MANUAL_QUESTIONS_ONLY, true)
                && !utilService.isQuestionTypeManuallyScored(question.objectType)) {
                return false;
            }

            if (isPrintOrPdf && !isNaN(selectedQuestionId) && item.contentRevisionId !== selectedQuestionId) {
                return false;
            }

            if (item.type === objectTypes.CATEGORY.MATCH_IN_STIMULUS) {
                return false;
            }

            return true;
        });
    }

    shouldRenderQuestionLevelStimulus(item) {
        const hasInlineStimulus = !!assessmentReader.getStimulusForSectionId(item?.sectionId);
        return uiService.isPbAndMultipleStimulusEnabled() && item?.stimulusItem?.contentRevisionId && !hasInlineStimulus;
    }

    initLoadTracker(itemsOrder) {
        const { shouldRenderTestCover } = this.props;
        this.itemTrackers = {};
        this.loadTracker = new LoadTracker((success) => {
            this.onReady(success);
        }, 'AllViewContainer');

        if (sessionReader.isContextViewOwnDelivery() || settingsReader.showTableOfContents()) {
            this.testOverviewTracker = this.loadTracker.addItemToTrack('TestOverview');
        }

        if (itemsOrder) {
            itemsOrder.forEach((item) => {
                const id = item.contentRevisionId;
                this.itemTrackers[id] = this.loadTracker.addItemToTrack(`StimulusOrQuestion-${id}`);
                if (this.shouldRenderQuestionLevelStimulus(item)) {
                    const stimulusId = item?.stimulusItem?.contentRevisionId;
                    this.itemTrackers[stimulusId] = this.loadTracker.addItemToTrack(`Stimulus-${stimulusId}`);
                }
            });
        }

        if (shouldRenderTestCover) {
            this.testCoverTracker = this.loadTracker.addItemToTrack('TestCover');
        }

        this.loadTracker.setAllItemsAdded();
    }

    renderTestOverview() {
        const { shouldRenderResultPageContainer, shouldRenderTableOfContents } = this.props;
        if (shouldRenderResultPageContainer) {
            return (
                <ResultPageContainer onReady={this.testOverviewTracker} />
            );
        }

        if (shouldRenderTableOfContents) {
            return (
                <TableOfContents onReady={this.testOverviewTracker} />
            );
        }

        return null;
    }

    renderTestCover() {
        const { shouldRenderTestCover } = this.props;
        if (shouldRenderTestCover) {
            return (
                <TestCover
                    deliveryDetails={this.props.deliveryDetails}
                    onReady={this.testCoverTracker}
                />
            );
        }

        return null;
    }

    renderTestItems() {
        const { shouldRenderTestItems } = this.props;

        if (!shouldRenderTestItems) {
            return null;
        }

        return this.filteredItemsOrder.map((item) => {
            if (this.shouldRenderQuestionLevelStimulus(item)) {
                // show question level stimulus only when no inline stimulus
                return (
                    <div key={`item_wrapper_${item?.contentRevisionId}`} className={styles.pbQuestionWithStimulus}>
                        <StimulusDisplayContainer
                            key={`${item?.stimulusItem?.contentRevisionId}_stimulus`}
                            onReady={this.itemTrackers[item?.stimulusItem?.contentRevisionId]}
                            sectionId={item?.sectionId}
                            questionContentRevisionId={item?.contentRevisionId}
                        />
                        <QuestionDisplayContainer
                            contentRevisionId={item?.contentRevisionId}
                            key={item?.contentRevisionId}
                            onReady={this.itemTrackers[item?.contentRevisionId]}
                            isAllView
                        />
                    </div>
                );
            } else if (item.type === objectTypes.CATEGORY.STIMULUS) {
                return (<StimulusDisplayContainer
                    key={`${item.contentRevisionId}_stimulus`}
                    onReady={this.itemTrackers[item.contentRevisionId]}
                    sectionId={item.sectionId}
                />);
            }
            return (<QuestionDisplayContainer
                contentRevisionId={item.contentRevisionId}
                key={item.contentRevisionId}
                onReady={this.itemTrackers[item.contentRevisionId]}
                isAllView
            />);
        });
    }

    renderAnswerKeys() {
        const { shouldRenderAnswerKeys } = this.props;
        return shouldRenderAnswerKeys && <AnswerKeys />;
    }

    render() {
        const { itemsOrder, extraClasses } = this.props;
        const classes = `${styles.allView} ${extraClasses}`;

        return (
            itemsOrder && <div className={classes} ref={(element) => { this.rootElement = element; }}>
                {this.renderTestCover()}
                {this.renderTestOverview()}
                {this.renderTestItems()}
                {this.renderAnswerKeys()}
            </div>
        );
    }
}

const mapStateToProps = state => allViewContainerSelector.selectProps(state);

const AllViewContainer = connect(mapStateToProps)(AllView);
export default AllViewContainer;
